import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface CampMasterListRecord {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  placeCode: string;
  placeName: string;
  startDate: string;
  endDate: string;
  newCamper?: boolean;
}

type SortField = 'firstName' | 'lastName' | 'email' | 'placeCode' | 'placeName' | 'startDate' | 'endDate' | 'newCamper';
type SortDirection = 'asc' | 'desc';

const CampMasterList: React.FC = () => {
  const [records, setRecords] = useState<CampMasterListRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState<SortField>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    fetchCampers();
  }, []);

  const fetchCampers = async () => {
    try {
      setLoading(true);
      setError('');
      const snapshot = await getDocs(collection(db, 'Registrations'));
      const campers: CampMasterListRecord[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.status === 'cancelled') return;
        const placeCode = data.financial?.accommodationFees?.assignedAccommodation || data.assignedAccommodation?.placeCode || data.accommodationFees?.placeCode || '';
        if (!placeCode) return; // Filter out campers with no place code
        const startDate = data.stayDetails?.startDate || data.selectedDates?.startDate || data.campDates?.arrivalDate || '';
        const endDate = data.stayDetails?.endDate || data.selectedDates?.endDate || data.campDates?.departureDate || '';
        
        // Extract name from different possible structures
        const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
        const nameParts = primaryCamperName.split(' ');
        const firstName = nameParts[0] || data.firstName || '';
        const lastName = nameParts.slice(1).join(' ') || data.lastName || '';
        
        campers.push({
          id: docSnap.id,
          firstName,
          lastName,
          email: data.registrationInfo?.email || data.email || '',
          placeCode,
          placeName: data.assignedAccommodation?.placeName || data.accommodationFees?.placeName || data.financial?.accommodationFees?.placeName || '',
          startDate,
          endDate,
          newCamper: !!data.NewCamper,
        });
      });
      setRecords(campers);
    } catch (err) {
      setError('Failed to load campers');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedRecords = () => {
    return [...records].sort((a, b) => {
      let aValue: any = a[sortField] || '';
      let bValue: any = b[sortField] || '';
      if (sortField === 'newCamper') {
        // Sort true before false for 'asc', false before true for 'desc'
        aValue = !!aValue;
        bValue = !!bValue;
        if (aValue === bValue) return 0;
        return sortDirection === 'asc' ? (aValue ? -1 : 1) : (aValue ? 1 : -1);
      }
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const exportToCSV = () => {
    if (!records.length) return;
    const headers = [
      'Last Name',
      'First Name',
      'Email',
      'Place Code',
      'Place Name',
      'New Camper',
      'Start Date',
      'End Date',
    ];
    const csvRows = [
      headers.join(','),
      ...getSortedRecords().map(record => [
        `"${record.lastName}"`,
        `"${record.firstName}"`,
        `"${record.email}"`,
        `"${record.placeCode}"`,
        `"${record.placeName}"`,
        record.newCamper ? 'Yes' : '',
        record.startDate || ( (!record.startDate && !record.endDate) ? 'Full Camp' : '' ),
        record.endDate || ( (!record.startDate && !record.endDate) ? 'Full Camp' : '' ),
      ].join(',')),
    ];
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `camp_master_list_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div>Loading campers...</div>;
  if (error) return <div className="error">{error}</div>;

  const sortedRecords = getSortedRecords();

  return (
    <div className="camp-master-list-report">
      <div className="report-header">
        <h2>Camp Master List</h2>
        <span className="record-count">{sortedRecords.length} camper(s)</span>
        <button onClick={exportToCSV} style={{ marginLeft: 16, padding: '6px 16px', fontSize: 14 }}>
          Export to CSV
        </button>
      </div>
      <table className="stats-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('lastName')} style={{ cursor: 'pointer' }}>
              Last Name {sortField === 'lastName' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('firstName')} style={{ cursor: 'pointer' }}>
              First Name {sortField === 'firstName' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
              Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('placeCode')} style={{ cursor: 'pointer' }}>
              Place Code {sortField === 'placeCode' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('placeName')} style={{ cursor: 'pointer' }} className="place-name">
              Place Name {sortField === 'placeName' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('newCamper')} style={{ cursor: 'pointer' }}>
              New Camper {sortField === 'newCamper' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('startDate')} style={{ cursor: 'pointer' }}>
              Start Date {sortField === 'startDate' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('endDate')} style={{ cursor: 'pointer' }}>
              End Date {sortField === 'endDate' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedRecords.map(record => {
            const showFullCamp = !record.startDate && !record.endDate;
            return (
              <tr key={record.id}>
                <td>{record.lastName}</td>
                <td>{record.firstName}</td>
                <td>{record.email}</td>
                <td>{record.placeCode}</td>
                <td className="place-name">{record.placeName}</td>
                <td style={{ textAlign: 'center' }}>{record.newCamper ? '✓' : ''}</td>
                <td>{showFullCamp ? 'Full Camp' : record.startDate}</td>
                <td>{showFullCamp ? 'Full Camp' : record.endDate}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CampMasterList; 